import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UserProvider } from "@/contexts/UserContext";
import ErrorBoundary from "@/components/ErrorBoundary";
const Landing = lazy(() => import("./pages/Landing"));
const PublicLayout = lazy(() => import("@/components/PublicLayout"));

const TopNav = lazy(() => import("@/components/TopNav"));
const BottomNav = lazy(() => import("@/components/BottomNav"));
const Today = lazy(() => import("./pages/Today"));
const Discover = lazy(() => import("./pages/Discover"));
const Make = lazy(() => import("./pages/Make"));
const Shelf = lazy(() => import("./pages/Shelf"));
const Profile = lazy(() => import("./pages/Profile"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const ChapterView = lazy(() => import("./pages/ChapterView"));
const ChainDetail = lazy(() => import("./pages/ChainDetail"));
const CollectionDetail = lazy(() => import("./pages/CollectionDetail"));
const OnboardingQuiz = lazy(() => import("./components/OnboardingQuiz"));
const RecommendationResults = lazy(() => import("./pages/RecommendationResults"));
const ContextLauncher = lazy(() => import("./pages/ContextLauncher"));
const TrackCompletion = lazy(() => import("./pages/TrackCompletion"));
const Legal = lazy(() => import("./pages/Legal"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AdminContentGen = lazy(() => import("./pages/AdminContentGen"));
const AdminVideos = lazy(() => import("./pages/AdminVideos"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const StartFlow = lazy(() => import("./pages/StartFlow"));

const queryClient = new QueryClient();

const PUBLIC_PAGES = ['/about', '/contact', '/how-it-works', '/pricing', '/blog', '/privacy', '/terms', '/legal'];

function AppLayout() {
  const location = useLocation();
  const isLanding = location.pathname === '/';
  const isPublicPage = PUBLIC_PAGES.includes(location.pathname) || location.pathname.startsWith('/blog/');
  const isChapterRoute = /^\/project\/[^/]+\/chapter\/\d+$/.test(location.pathname);
  const isFullScreen = ['/onboarding', '/start', '/recommendations', '/auth', '/reset-password'].includes(location.pathname)
    || location.pathname.endsWith('/complete')
    || location.pathname.startsWith('/admin')
    || isChapterRoute;

  if (isLanding) return <Suspense fallback={null}><main><Landing /></main></Suspense>;

  if (isPublicPage) {
    return (
      <Suspense fallback={null}>
        <PublicLayout>
          <Routes>
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/legal" element={<Legal />} />
          </Routes>
        </PublicLayout>
      </Suspense>
    );
  }

  if (isFullScreen) {
    return (
      <Suspense fallback={null}>
        <main className={isChapterRoute ? 'h-screen overflow-hidden' : undefined}>
          <Routes>
            <Route path="/start" element={<StartFlow />} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingQuiz /></ProtectedRoute>} />
            <Route path="/recommendations" element={<ProtectedRoute><RecommendationResults /></ProtectedRoute>} />
            <Route path="/project/:id/complete" element={<ProtectedRoute><TrackCompletion /></ProtectedRoute>} />
            <Route path="/project/:id/chapter/:chapterIdx" element={<ProtectedRoute><ChapterView /></ProtectedRoute>} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin/content" element={<ProtectedRoute><AdminContentGen /></ProtectedRoute>} />
            <Route path="/admin/videos" element={<ProtectedRoute><AdminVideos /></ProtectedRoute>} />
          </Routes>
        </main>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <TopNav />
      <main className="min-h-screen pb-20">
        <Routes>
          <Route path="/today" element={<ProtectedRoute><Today /></ProtectedRoute>} />
          <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
          <Route path="/make" element={<ProtectedRoute><Make /></ProtectedRoute>} />
          <Route path="/shelf" element={<ProtectedRoute><Shelf /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/tonight" element={<ProtectedRoute><ContextLauncher /></ProtectedRoute>} />
          <Route path="/project/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
          <Route path="/chain/:id" element={<ProtectedRoute><ChainDetail /></ProtectedRoute>} />
          <Route path="/collection/:id" element={<ProtectedRoute><CollectionDetail /></ProtectedRoute>} />
          <Route path="/home" element={<Navigate to="/today" replace />} />
          <Route path="/explore" element={<Navigate to="/discover" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <BottomNav />
    </Suspense>
  );
}

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Sonner />
          <UserProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/*" element={<AppLayout />} />
              </Routes>
            </BrowserRouter>
          </UserProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
